import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ChevronDown, Download, FileText, Loader2 } from 'lucide-react';
import MultiPicker from './MultiPicker';

const fmtInr = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const dayLabel = (iso) => {
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }); }
  catch { return iso; }
};

const GROUPS = [['day', 'Day'], ['salesperson', 'Salesperson'], ['client_type', 'Client Type'], ['customer', 'Customer']];
const iso = (offset) => { const dt = new Date(); dt.setDate(dt.getDate() + offset); return dt.toISOString().slice(0, 10); };
const PRESETS = [
  { id: 'today', label: 'Today', params: () => ({ from_date: iso(0), to_date: iso(0) }) },
  { id: 'yesterday', label: 'Yesterday', params: () => ({ from_date: iso(-1), to_date: iso(-1) }) },
  { id: 'last7', label: 'Last 7 days', params: () => ({ from_date: iso(-6), to_date: iso(0) }) },
  { id: 'last10', label: 'Last 10 days', params: () => ({ from_date: iso(-9), to_date: iso(0) }) },
  { id: 'last15', label: 'Last 15 days', params: () => ({ from_date: iso(-14), to_date: iso(0) }) },
  { id: 'last30', label: 'Last 30 days', params: () => ({ from_date: iso(-29), to_date: iso(0) }) },
];

export const PaymentsReceivedModal = ({ open, onOpenChange, zohoOrgId }) => {
  const [preset, setPreset] = useState('last7');
  const [custom, setCustom] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [groupBy, setGroupBy] = useState('day');
  const [includeClientType, setIncludeClientType] = useState([]);
  const [includeSalesperson, setIncludeSalesperson] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let params;
      if (custom && customFrom && customTo) params = { from_date: customFrom, to_date: customTo };
      else params = (PRESETS.find((p) => p.id === preset) || PRESETS[2]).params();
      if (includeClientType.length) params.include_client_type = includeClientType.join(',');
      if (includeSalesperson.length) params.include_salesperson = includeSalesperson.join(',');
      const r = await api.get('/books/payments-received', { params });
      setData(r.data);
      setExpanded({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [preset, custom, customFrom, customTo, includeClientType, includeSalesperson]);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  const groups = useMemo(() => {
    if (!data) return [];
    const keyOf = (p) => groupBy === 'day' ? p.date
      : groupBy === 'salesperson' ? p.salesperson_name
        : groupBy === 'client_type' ? p.client_type : p.customer_name;
    const m = new Map();
    for (const pm of data.payments) {
      const k = keyOf(pm) || '(Unknown)';
      if (!m.has(k)) m.set(k, { key: k, payments: [], total: 0, count: 0 });
      const g = m.get(k);
      g.payments.push(pm);
      g.total += pm.amount;
      g.count += 1;
    }
    const arr = [...m.values()];
    if (groupBy === 'day') arr.sort((a, b) => b.key.localeCompare(a.key));
    else arr.sort((a, b) => b.total - a.total);
    return arr;
  }, [data, groupBy]);

  const exportCsv = () => {
    if (!data) return;
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const inr = (n) => Math.round(n || 0);
    const lines = [];
    lines.push(esc('Payments Received'));
    lines.push(esc(`Window: ${data.window.from} to ${data.window.to}`));
    lines.push(esc(`Grouped by: ${GROUPS.find(([v]) => v === groupBy)?.[1] || groupBy}`));
    lines.push(esc(`Generated: ${new Date().toLocaleString('en-IN')}`));
    lines.push('');
    const s = data.summary;
    lines.push(['KPI', 'Value', 'Notes'].map(esc).join(','));
    lines.push(['Total received', inr(s.total), `${s.count} payment(s)`].map(esc).join(','));
    lines.push(['Unique customers', s.unique_customers, ''].map(esc).join(','));
    lines.push(['Average payment', inr(s.avg_payment), ''].map(esc).join(','));
    lines.push('');
    const headers = ['S.No', 'Group', 'Date', 'Payment #', 'Customer', 'Amount', 'Mode', 'Reference #', 'Salesperson', 'Client Type', 'Branch', 'Invoice(s)', 'Enrolment(s)'];
    lines.push(headers.map(esc).join(','));
    let sn = 0;
    groups.forEach((g) => {
      const label = groupBy === 'day' ? dayLabel(g.key) : g.key;
      g.payments.forEach((p) => {
        sn += 1;
        lines.push([sn, label, p.date, p.payment_number, p.customer_name, inr(p.amount), p.mode || '', p.reference_number || '', p.salesperson_name || '', p.client_type || '', p.branch_name || '', (p.invoice_numbers || []).join('; '), (p.enrolment_nos || []).join('; ')].map(esc).join(','));
      });
      lines.push(['', `${label} subtotal`, '', '', `${g.count} payment(s)`, inr(g.total), '', '', '', '', '', '', ''].map(esc).join(','));
    });
    lines.push('');
    lines.push(['', 'GRAND TOTAL', '', '', `${s.count} payment(s)`, inr(s.total), '', '', '', '', '', '', ''].map(esc).join(','));
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `payments-received-${data.window.from}-to-${data.window.to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportPdf = async () => {
    if (!data) return;
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 28;
    const inrTxt = (n) => `Rs. ${Math.round(n || 0).toLocaleString('en-IN')}`;
    const groupLabel = GROUPS.find(([v]) => v === groupBy)?.[1] || groupBy;
    const s = data.summary;

    doc.setFillColor(6, 78, 59);
    doc.rect(0, 0, pageW, 56, 'F');
    doc.setFillColor(16, 185, 129);
    doc.rect(0, 56, pageW, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
    doc.text('Payments Received', margin, 28);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    doc.setTextColor(209, 250, 229);
    doc.text(`${data.window.from}  to  ${data.window.to}   |   Grouped by: ${groupLabel}`, margin, 46);
    doc.setFontSize(8);
    doc.text(`Generated ${new Date().toLocaleString('en-IN')}`, pageW - margin, 46, { align: 'right' });

    const kpiY = 74;
    const kpiH = 52;
    const gap = 10;
    const kpiW = (pageW - margin * 2 - gap * 3) / 4;
    const kpis = [
      { label: 'TOTAL RECEIVED', value: inrTxt(s.total), sub: `${s.count} payment(s)`, fill: [209, 250, 229], border: [16, 185, 129], text: [6, 78, 59] },
      { label: 'PAYMENT COUNT', value: `${s.count}`, sub: `in ${data.window.from} → ${data.window.to}`, fill: [219, 234, 254], border: [59, 130, 246], text: [30, 58, 138] },
      { label: 'UNIQUE CUSTOMERS', value: `${s.unique_customers}`, sub: 'distinct customer_ids', fill: [237, 233, 254], border: [139, 92, 246], text: [76, 29, 149] },
      { label: 'AVG PAYMENT', value: inrTxt(s.avg_payment), sub: 'total / count', fill: [254, 243, 199], border: [245, 158, 11], text: [120, 53, 15] },
    ];
    kpis.forEach((k, i) => {
      const x = margin + i * (kpiW + gap);
      doc.setFillColor(...k.fill);
      doc.setDrawColor(...k.border);
      doc.setLineWidth(0.8);
      doc.roundedRect(x, kpiY, kpiW, kpiH, 4, 4, 'FD');
      doc.setTextColor(...k.text);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7);
      doc.text(k.label, x + 8, kpiY + 12);
      doc.setFontSize(13);
      doc.text(k.value, x + 8, kpiY + 30);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
      doc.setTextColor(71, 85, 105);
      doc.text(k.sub, x + 8, kpiY + 44);
    });

    const head = [['#', 'Date', 'Payment #', 'Customer', 'Salesperson', 'Client Type', 'Mode', 'Invoice(s)', 'Amount']];
    const body = [];
    let sn = 0;
    groups.forEach((g) => {
      const label = groupBy === 'day' ? dayLabel(g.key) : g.key;
      body.push([{ content: `${label}   -   ${g.count} payment(s)   |   Total ${Math.round(g.total).toLocaleString('en-IN')}`, colSpan: 9, styles: { fillColor: [220, 252, 231], textColor: [6, 78, 59], fontStyle: 'bold', fontSize: 9, cellPadding: 5 } }]);
      g.payments.forEach((p) => {
        sn += 1;
        body.push([sn, p.date, p.payment_number, p.customer_name || '', p.salesperson_name || '', p.client_type || '', p.mode || '', (p.invoice_numbers || []).join(', '), Math.round(p.amount).toLocaleString('en-IN')]);
      });
    });
    body.push([
      { content: `GRAND TOTAL - ${s.count} payment(s)`, colSpan: 8, styles: { fillColor: [6, 78, 59], textColor: 255, fontStyle: 'bold', halign: 'right', fontSize: 10 } },
      { content: Math.round(s.total).toLocaleString('en-IN'), styles: { fillColor: [6, 78, 59], textColor: [134, 239, 172], fontStyle: 'bold', halign: 'right', fontSize: 10 } },
    ]);

    autoTable(doc, {
      startY: kpiY + kpiH + 14,
      head, body,
      margin: { left: margin, right: margin, bottom: 28 },
      styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak', valign: 'middle' },
      headStyles: { fillColor: [16, 185, 129], textColor: 255, fontStyle: 'bold', halign: 'center' },
      columnStyles: {
        0: { halign: 'right', cellWidth: 24 },
        1: { cellWidth: 58 },
        2: { cellWidth: 60 },
        3: { cellWidth: 170 },
        4: { cellWidth: 84 },
        5: { cellWidth: 66 },
        6: { cellWidth: 110 },
        7: { cellWidth: 130 },
        8: { halign: 'right', cellWidth: 72 },
      },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      didDrawPage: () => {
        doc.setFontSize(7); doc.setTextColor(148, 163, 184);
        doc.text('Payments Received  |  Confidential', margin, pageH - 14);
        doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageW - margin, pageH - 14, { align: 'right' });
      },
    });
    doc.save(`payments-received-${data.window.from}-to-${data.window.to}.pdf`);
  };

  const pmtRow = (pm, key, sn) => (
    <div key={key} className="grid grid-cols-[repeat(14,minmax(0,1fr))] gap-2 px-3 py-1.5 border-t border-slate-100 text-[11px] items-center hover:bg-emerald-50/50">
      <div className="text-slate-400 tabular-nums">{sn}</div>
      <div className="text-slate-500 whitespace-nowrap text-[10px]">{pm.date}</div>
      <div className="font-medium text-slate-700 whitespace-nowrap">#{pm.payment_number}</div>
      <div className="col-span-3 text-slate-700 truncate" title={pm.customer_name}>{pm.customer_name}</div>
      <div className="col-span-2 text-slate-500 truncate text-[10px]" title={pm.mode}>{pm.mode || '—'}</div>
      <div className="text-slate-400 truncate text-[10px]" title={pm.reference_number}>{pm.reference_number || '—'}</div>
      <div className="text-slate-500 truncate text-[10px]" title={pm.salesperson_name}>{pm.salesperson_name}</div>
      <div className="text-slate-500 truncate text-[10px]" title={pm.client_type}>{pm.client_type}</div>
      <div className="text-slate-500 truncate text-[10px]" title={(pm.invoice_numbers || []).join(', ')}>{(pm.invoice_numbers || []).slice(0, 2).join(', ')}{(pm.invoice_numbers || []).length > 2 ? ` +${pm.invoice_numbers.length - 2}` : ''}</div>
      <div className="text-right tabular-nums font-semibold text-emerald-700">{fmtInr(pm.amount)}</div>
      <div className="text-right">
        {zohoOrgId && <a href={`https://books.zoho.com/app/${zohoOrgId}/#/paymentsreceived/${pm.payment_id}`} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline text-[10px] font-medium" data-testid={`pmt-link-${pm.payment_number}`}>Open ↗</a>}
      </div>
    </div>
  );

  const s = data?.summary;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="payments-received-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">💰 Payments Received
            <span className="text-[10px] font-normal text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">Historical collections view</span>
          </DialogTitle>
          {data && <p className="text-[11px] text-slate-500 mt-0.5">Received {data.window.from} → {data.window.to} · sourced from Zoho Books customer payments</p>}
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((pr) => (
            <button key={pr.id} type="button" onClick={() => { setCustom(false); setPreset(pr.id); }}
              data-testid={`pmt-preset-${pr.id}`}
              className={`text-[11px] px-2.5 py-1 rounded-md border transition ${!custom && preset === pr.id ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{pr.label}</button>
          ))}
          <button type="button" onClick={() => setCustom(true)} data-testid="pmt-preset-custom"
            className={`text-[11px] px-2.5 py-1 rounded-md border transition ${custom ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Custom</button>
          {custom && (
            <span className="flex items-center gap-1">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="text-[11px] border border-slate-200 rounded-md px-1.5 py-0.5" data-testid="pmt-custom-from" />
              <span className="text-slate-400 text-[10px]">→</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="text-[11px] border border-slate-200 rounded-md px-1.5 py-0.5" data-testid="pmt-custom-to" />
            </span>
          )}
          <span className="ml-auto flex items-center gap-1.5">
            <MultiPicker label="Client Type" testid="pmt-include-client-type" width="w-32"
              options={data?.client_types || []} value={includeClientType} onChange={setIncludeClientType}
              accent="emerald" />
            <MultiPicker label="Salesperson" testid="pmt-include-salesperson" width="w-40"
              options={data?.salespeople || []} value={includeSalesperson} onChange={setIncludeSalesperson}
              accent="emerald" />
            <span className="text-[10px] uppercase tracking-wide text-slate-400">Group by</span>
            {GROUPS.map(([v, l]) => (
              <button key={v} type="button" onClick={() => setGroupBy(v)} data-testid={`pmt-group-${v}`}
                className={`text-[11px] px-2 py-1 rounded-md border transition ${groupBy === v ? 'bg-violet-600 text-white border-violet-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
            ))}
            <button type="button" onClick={() => setExpanded({})} data-testid="pmt-expand-all"
              className="text-[11px] px-2 py-1 rounded-md border border-slate-200 text-slate-600 bg-white hover:bg-slate-50">
              Expand all
            </button>
            <button type="button" onClick={() => setExpanded(Object.fromEntries(groups.map((g) => [g.key, false])))} data-testid="pmt-collapse-all"
              className="text-[11px] px-2 py-1 rounded-md border border-slate-200 text-slate-600 bg-white hover:bg-slate-50">
              Collapse all
            </button>
            <button type="button" onClick={exportCsv} data-testid="pmt-export-csv"
              className="text-[11px] px-2.5 py-1 rounded-md border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 flex items-center gap-1">
              <Download className="w-3 h-3" /> CSV
            </button>
            <button type="button" onClick={exportPdf} data-testid="pmt-export-pdf"
              className="text-[11px] px-2.5 py-1 rounded-md border border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100 flex items-center gap-1">
              <FileText className="w-3 h-3" /> PDF
            </button>
          </span>
        </div>

        {s && (
          <div className="grid grid-cols-4 gap-2" data-testid="pmt-summary">
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
              <p className="text-[9px] uppercase tracking-wide text-emerald-600 font-semibold">Total received</p>
              <p className="text-sm font-bold text-emerald-800 tabular-nums">{fmtInr(s.total)} <span className="text-[10px] font-normal text-slate-500">· {s.count} pmt</span></p>
            </div>
            <div className="bg-sky-50 border border-sky-100 rounded-lg px-3 py-2">
              <p className="text-[9px] uppercase tracking-wide text-sky-600 font-semibold">Payment count</p>
              <p className="text-sm font-bold text-sky-800 tabular-nums">{s.count}</p>
            </div>
            <div className="bg-violet-50 border border-violet-100 rounded-lg px-3 py-2">
              <p className="text-[9px] uppercase tracking-wide text-violet-600 font-semibold">Unique customers</p>
              <p className="text-sm font-bold text-violet-800 tabular-nums">{s.unique_customers}</p>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <p className="text-[9px] uppercase tracking-wide text-amber-600 font-semibold">Average payment</p>
              <p className="text-sm font-bold text-amber-800 tabular-nums">{fmtInr(s.avg_payment)}</p>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-emerald-600" /></div>
          ) : !data ? null : groups.length === 0 ? (
            <p className="text-xs text-slate-400 py-10 text-center">No payments received in this window.</p>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-[repeat(14,minmax(0,1fr))] gap-2 px-3 py-1.5 bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 font-medium sticky top-0 z-10">
                <div>#</div><div>Date</div><div>Pmt #</div><div className="col-span-3">Customer</div>
                <div className="col-span-2">Mode</div><div>Ref #</div><div>Salesperson</div><div>Client Type</div><div>Invoice(s)</div>
                <div className="text-right">Amount</div><div />
              </div>
              {groups.map((g, gi) => {
                const isOpen = expanded[g.key] !== false;
                const offset = groups.slice(0, gi).reduce((a, x) => a + x.payments.length, 0);
                return (
                  <React.Fragment key={g.key}>
                    <button type="button" onClick={() => setExpanded((p) => ({ ...p, [g.key]: !isOpen }))}
                      data-testid={`pmt-grp-${g.key}`}
                      className="w-full px-3 py-2 bg-emerald-50/70 border-t border-emerald-100 text-left hover:bg-emerald-100/60 transition">
                      <div className="flex items-center gap-2 text-[11px]">
                        <ChevronDown className={`w-3 h-3 shrink-0 text-emerald-500 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                        <span className="font-semibold text-emerald-900">{groupBy === 'day' ? dayLabel(g.key) : g.key}</span>
                        <span className="text-slate-500">· {g.count} pmt</span>
                        <span className="ml-auto tabular-nums text-emerald-800 font-semibold">{fmtInr(g.total)}</span>
                      </div>
                    </button>
                    {isOpen && g.payments.map((pm, ii) => pmtRow(pm, `${g.key}-${pm.payment_id}`, offset + ii + 1))}
                  </React.Fragment>
                );
              })}
              <div className="grid grid-cols-[repeat(14,minmax(0,1fr))] gap-2 px-3 py-2 bg-slate-50 border-t-2 border-slate-300 text-[11px] items-center font-bold" data-testid="pmt-total-row">
                <div className="col-span-12 text-slate-800">GRAND TOTAL — {s.count} payment(s)</div>
                <div className="text-right tabular-nums text-emerald-700">{fmtInr(s.total)}</div>
                <div />
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentsReceivedModal;
